import type { IssuanceMetadataPaths } from './issuance.js';

type IgnoredFields
  = | 'accountId'
    | 'adjustedLCAValue'
    | 'sustainabilityTier';

type AdditionalFields
  = | 'blending.blendingLocation'
    | 'blending.blendingLocation.country'
    | 'supplier.siteAddress'
    | 'supplier.siteAddress.country'
    | 'greenhouseGas'
    | 'batch'
    | 'certification'
    | 'supplier'
    | 'blending'
    | 'product';

/** The `required` does not mean validation (done by zod). It only prints asterix */
export const issueFields: Record<Exclude<IssuanceMetadataPaths, IgnoredFields> | AdditionalFields, { title: string; placeholder?: string; tootlipText?: string; description?: string; required?: boolean }> = {
  'assuranceLevel': {
    title: 'Assurance level',
  },
  'adjustedLCAChoice': {
    title: 'Adjusted LCA choice',
  },
  'documentationType': {
    title: 'Are you submitting a Proof of Sustainability (POS) \n or Proof of Compliance (POC)?',
    tootlipText: 'Upload either the POS or POC of the SAF batch',
    required: true,
  },
  'fileTool': {
    title: 'Supporting documentation upload',
    description: 'Upload your Proof of Sustainability or Proof of Compliance document. \n Accepts file upload in appropriate formats PDF or CSV. Max 10MB',
    required: true,
  },
  'batch': {
    title: 'Batch Tab',
  },
  'batch.companyName': {
    title: 'Fuel producing company name',
    placeholder: 'Fuel producing company name',
    tootlipText: 'Enter name of company that produced the batch of SAF',
    required: true,
  },
  'batch.fuelProviderCompanyName': {
    title: 'Fuel provider company name',
    placeholder: 'Company name',
    tootlipText: 'The name of your company. If you’re also the fuel producer, this will be the same as the Fuel Producing company name.',
    required: true,
  },
  'batch.leaveDate': {
    title: 'Date of SAF leaving the issuer\'s \n chain of custody',
    placeholder: 'Date',
    required: true,
  },
  'batch.batchNumber': {
    title: 'Batch Number of SAF',
    placeholder: 'Batch Number of SAF',
    required: true,
  },
  'batch.productionYear': {
    title: 'Year of SAF production',
    placeholder: 'Year',
    required: true,
  },
  'batch.upliftCountry': {
    title: 'Country of SAF uplift',
    placeholder: 'Country of SAF uplift',
    tootlipText: 'Enter country where the batch of SAF will be delivered to an airport and uplifted.',
  },
  'batch.upliftAirport': {
    title: 'Airport of SAF uplift',
    placeholder: 'Airport of SAF uplift',
    tootlipText: 'Enter name of airport where the batch of SAF will be uplifted from. Also, enter in parenthesis the ICAO airport code if known [for example, Heathrow (EGLL)]',
  },
  'batch.regulatorySchemesDeclaration': {
    title: 'Regulatory schemes applicable \n to the production of the SAF \n (select one or more)',
    tootlipText: 'If a scheme with "*" and SABA additionality approach (below) are selected, the SAFc will only be valid for compliance.',
    placeholder: 'Regulatory schemes',
    required: true,
  },
  'batch.additionalityApproach.type': {
    title: 'Additionality approach applied to the SAF',
    placeholder: 'Additionality approach',
    tootlipText: 'What additionality approach will you apply to the SAF',
    required: true,
  },
  'batch.additionalityApproach.allowScope3Claims': {
    title: 'Does the Non-SABA additionality approach applied to the SAF allow for \'scope 3\' claims to be made?',
    required: true,
  },
  'batch.additionalityApproach.approachName': {
    title: 'Non-SABA Additionality Approaches',
    tootlipText: 'The following is not an exhaustive list - IATA Additionality Approach/ISCC Additionality Approach/RSB Additionality Approach/Smart Freight Center Additionality Approach',
    required: true,
  },
  'blending': {
    title: 'Blending Tab',
  },
  'blending.isBlended': {
    title: 'Is the SAF blended with conventional \n aviation fuel or certified as drop-in \n according to ASTM Standard D7566 or D1655?',
    required: true,
  },
  'blending.blendingDate': {
    title: 'Date of SAF blending with conventional jet fuel \n or certification as drop-in?',
    placeholder: 'Date',
    tootlipText: 'Enter date batch of SAF was blended with jet fuel or certified to ASTM D7566 or D1655',
    required: true,
  },
  'blending.blendingLocation': {
    title: 'Location of SAF blending with conventional jet fuel \n or certification as drop-in',
    tootlipText: 'Enter full address of the facility where the SAF batch was blended or certified to ASTM D7566 or D1655',
    required: true,
  },
  'blending.blendingLocation.addressLine': { title: 'Location of SAF blending (address)' },
  'blending.blendingLocation.city': { title: 'Location of SAF blending (city)' },
  'blending.blendingLocation.country': { title: 'Location of SAF blending (country)' },
  'blending.blendingLocation.country.code': { title: 'Location of SAF blending (country)' },
  'blending.blendingLocation.country.name': { title: 'Location of SAF blending (country)' },
  'blending.blendingLocation.postalCode': { title: 'Location of SAF blending (postal code)' },
  'blending.blendingLocation.state': { title: 'Location of SAF blending (state)' },
  'supplier': {
    title: 'Supplier Tab',
  },
  'supplier.siteName': {
    title: 'Fuel producing facility name',
    placeholder: 'Facility name',
    tootlipText: 'Enter name of facility where the SAF batch was produced',
    required: true,
  },
  'supplier.siteAddress': {
    title: 'Fuel producing facility address',
    required: true,
  },
  'supplier.siteAddress.addressLine': { title: 'Fuel producing facility address (address)' },
  'supplier.siteAddress.city': { title: 'Fuel producing facility address (city)' },
  'supplier.siteAddress.country': { title: 'Fuel producing facility address (country)' },
  'supplier.siteAddress.country.code': { title: 'Fuel producing facility address (country)' },
  'supplier.siteAddress.country.name': { title: 'Fuel producing facility address (country)' },
  'supplier.siteAddress.postalCode': { title: 'Fuel producing facility address (postal code)' },
  'supplier.siteAddress.state': { title: 'Fuel producing facility address (state)' },
  'certification': {
    title: 'Certification Tab',
  },
  'certification.scheme': {
    title: 'Fuel certification scheme \n (refer to the certification that is applicable \n to this batch of SAF)',
    placeholder: 'Fuel certification scheme',
    tootlipText: 'Select the primary fuel certification scheme applicable to the SAF batch. In the event that your supply chain has multiple valid certifications, enter the certification that matches the LCA methodology of the values reflected in your POS or POC documentation here.',
    required: true,
  },
  'certification.hasIRS7210ForIsccPlus': {
    title: 'Is there an accompanying IRS form 7210 and associated verification report (or comparable international requirement) for the SAF\'s ISCC PLUS certification?',
    required: true,
  },
  'certification.hasSeparateRFNBORCF': {
    title: 'Is there a separate document for the RFNBO/RCF requirements?',
    required: true,
  },
  'certification.type': {
    title: 'Actual or Default LCA?',
    tootlipText: 'Select whether default or actual LCA values are used for the SAF batch',
    required: true,
  },
  'certification.certificateNumber': {
    title: 'Certification number of the company \n that has produced the SAF',
    placeholder: 'Certification number',
    tootlipText: 'Enter sustainability certificate number of the SAF batch. This certificate might be the same as the RSB Trader certificate if the SAF provider is also the producer.',
    required: true,
  },
  'certification.certificationBody': {
    title: 'Name of the certification body \n responsible for the certification of the company \n that has produced the SAF',
    placeholder: 'Name of the certification body',
    tootlipText: 'Enter name of entity that certified the SAF batch (e.g. SCS Global, Control Union). This certificate might be the same as the RSB Trader certificate if the SAF provider is also the producer.',
    required: true,
  },
  'certification.expiryDate': {
    title: 'Expiry date of the certification of the company \n that has produced the SAF',
    placeholder: 'Expiry date',
    tootlipText: 'Enter expiry date of the sustainability certificate of the SAF batch. This certificate might be the same as the RSB Trader certificate if the SAF provider is also the producer.',
    required: true,
  },
  'certification.certificationBodyEmail': {
    title: 'Contact email of the certification body \n responsible for the certification of the company \n that has produced the SAF',
    placeholder: 'Contact email',
    tootlipText: 'Enter contact email of entity that certified the SAF batch (e.g. SCS Global, Control Union)',
    required: true,
  },
  'certification.traderCertificationNumber': {
    title: 'RSB Book & Claim Trader certification number, \n or ISCC (mass balance) Trader certification number, \n or ClassNK CORSIA (mass balance) \n Trader certification number of your company',
    placeholder: 'Trader certification number',
    required: true,
  },
  'certification.additionalScheme': {
    title: 'Any additional fuel certification schemes, \n standards and evaluation methodologies',
    description: 'Leave empty if not applicable',
    placeholder: 'Additional fuel certification scheme',
    tootlipText: 'Enter any other fuel certification schemes, standards and/or evaluation methods that apply to the SAF batch',
  },
  'certification.additionalHasIRS7210ForIsccPlus': {
    title: 'Is there an accompanying IRS form 7210 and associated verification report (or comparable international requirement) for the SAF\'s ISCC PLUS certification?',
    required: true,
  },
  'certification.additionalHasSeparateRFNBORCF': {
    title: 'Is there a separate document for the RFNBO/RCF requirements?',
    required: true,
  },
  'certification.additionalSchemeName': {
    title: 'Additional fuel certification scheme name. \n Please, specify:',
    placeholder: 'Certification scheme name',
    required: true,
  },
  'certification.additionalCertificateNumber': {
    title: 'Certification number of the company \n that has produced the SAF (additional scheme)',
    placeholder: 'Certification number (additional scheme)',
    tootlipText: 'Enter sustainability certificate number of the SAF batch. This certificate might be the same as the RSB Trader certificate if the SAF provider is also the producer.',
    required: true,
  },
  'certification.additionalExpiryDate': {
    title: 'Expiry date of the certification of the company \n that has produced the SAF (additional scheme)',
    placeholder: 'Expiry date (additional scheme)',
    tootlipText: 'Enter expiry date of the sustainability certificate of the SAF batch. This certificate might be the same as the RSB Trader certificate if the SAF provider is also the producer.',
    required: true,
  },
  'certification.additionalCertificationBody': {
    title: 'Name of the certification body \n responsible for the certification of the company \n that has produced the SAF (additional scheme)',
    placeholder: 'Name of the certification body (additional scheme)',
    tootlipText: 'Enter name of entity that certified the SAF batch (e.g. SCS Global, Control Union). This certificate might be the same as the RSB Trader certificate if the SAF provider is also the producer.',
    required: true,
  },
  'certification.additionalCertificationBodyEmail': {
    title: 'Contact email of the certification body \n responsible for the certification of the company \n that has produced the SAF (additional scheme)',
    placeholder: 'Contact email (additional scheme)',
    tootlipText: 'Enter contact email of entity that certified the SAF batch (e.g. SCS Global, Control Union)',
    required: true,
  },
  'certification.isLowILUCRiskConfirmed': {
    title: 'Was the feedstock or SAF certified as low ILUC risk?',
    tootlipText: 'Select if the SAF batch was confirmed to have low ILUC risk by an audit – i.e. a low ILUC risk certification to RSB or ISCC standards.\n\n If selection is \'no\', the SAF certificate will not qualify for the SABA-advanced or SABA-eligible sustainability tiers.',
    required: true,
  },
  'certification.lowRiskFollowUnusedLandApproach': {
    title: 'Did low ILUC risk certification follow the CORSIA \n "unused land approach", rather than the \n "yield increase approach"?',
    required: true,
  },
  'certification.lowRiskAuditCertificateNumber': {
    title: 'Certification number \n of low-ILUC risk audit',
    placeholder: 'Certification number (low-ILUC)',
    required: true,
  },
  'certification.lowRiskAuditExpiryDate': {
    title: 'Expiry date of the certification \n of low-ILUC risk audit',
    placeholder: 'Expiry date (low-ILUC)',
    required: true,
  },
  'certification.lowRiskAuditCertificationBody': {
    title: 'Name of the certification body responsible \n for the low-ILUC risk audit',
    placeholder: 'Name of the certification body (low-ILUC)',
    required: true,
  },
  'certification.lowRiskAuditCertificationBodyEmail': {
    title: 'Contact email of certification body responsible \n for the low-ILUC risk audit',
    placeholder: 'Contact email (low-ILUC)',
    required: true,
  },
  'certification.isLowDisplacementRiskConfirmed': {
    title: 'Was low non-ILUC displacement risk confirmed \n by an audit per the methodology for displacement \n emissions under RSB’s Standard for \n Advanced Biofuels?',
    tootlipText: 'Select if the SAF batch was confirmed to have low displacement risk by an audit – i.e. a low displacement risk evaluation to the RSB methodology.\n\nIf selection is \'no\', the SAF certificate will not qualify for the SABA-advanced sustainability tier',
    required: true,
  },
  'certification.lowDisplacementRiskAuditCertificateNumber': {
    title: 'Certification number \n of low-displacement risk audit',
    placeholder: 'Certification number (low-displacement)',
    required: true,
  },
  'certification.lowDisplacementRiskAuditExpiryDate': {
    title: 'Expiry date of the certification \n of low-displacement risk audit',
    placeholder: 'Expiry date (low-displacement)',
    required: true,
  },
  'certification.lowDisplacementRiskAuditCertificationBody': {
    title: 'Name of the certification body responsible \n for the low-displacement risk audit',
    placeholder: 'Name of the certification body (low-displacement)',
    required: true,
  },
  'certification.lowDisplacementRiskAuditCertificationBodyEmail': {
    title: 'Contact email of certification body \n for the low-displacement risk audit',
    placeholder: 'Contact email (low-displacement)',
    required: true,
  },
  'product': {
    title: 'Product information Tab',
  },
  'product.feedStock.name': {
    title: 'The feedstock used to make the SAF',
    placeholder: 'Feedstock name',
    required: true,
  },
  'product.feedStock.otherFeedStock': {
    title: 'Other feedstock. Please, specify:',
    placeholder: 'Feedstock name',
    required: true,
  },
  'product.feedStock.wasCertifiedAsProductionResidue': {
    title: 'Is the feedstock a waste, by-product or residue?',
    required: true,
  },
  'product.feedStock.isSolidWaste': {
    title: 'Is feedstock municipal solid waste?',
    required: true,
  },
  'product.feedStock.isEnergyCrop': {
    title: 'Is this an energy crop?',
    required: true,
  },
  'product.feedStock.isLanduseBasedCropBased': {
    title: 'Is the feedstock land-use based and crop-based?',
    required: true,
  },
  'product.feedStock.isCorsiaDefaultIlucLcaLteZero': {
    title: 'Does the feedstock have a CORSIA default ILUC LCA value less than or equal to zero?',
    required: true,
  },
  'product.feedStock.isInEuRed2Annex9PartA': {
    title: 'Is the feedstock included on EU RED II\'s Annex IX Part A?',
    required: true,
  },
  'product.volumeOfNeatFuel': {
    title: 'Volume of neat fuel',
    placeholder: 'Volume of neat fuel',
  },
  'product.volumeOfNeatFuelUnit': {
    title: 'Volume of neat fuel unit',
    required: true,
  },
  'product.quantityMT': {
    title: 'Mass of neat SAF',
    placeholder: 'Mass of neat SAF',
    required: true,
  },
  'product.isFeedStockMsw': {
    title: 'If municipal solid waste,\n % of non-biological content',
    placeholder: '% of content',
    required: true,
  },
  'product.countries': {
    title: 'Feedstock country or countries of origin',
    placeholder: 'Country or countries of origin',
    required: true,
  },
  'product.conversionProcess': {
    title: 'SAF conversion process',
    placeholder: 'SAF conversion process',
    required: true,
  },
  'product.lowerHeatingValue': {
    title: 'SAF lower heating value',
    placeholder: 'Heating value',
    required: true,
  },
  'product.jetFuelBaselineCarbonIntensity': {
    title: 'Conventional jet fuel baseline \n carbon intensity (gCO2e/MJ)',
    description: 'Well to wake',
    placeholder: 'Jet fuel baseline carbon intensity',
    tootlipText: 'Select the carbon intensity of conventional jet fuel used as baseline to calculate LCA of the SAF batch. This should be specified by the fuel certification scheme used to verify the fuel’s carbon intensity.\n\n• If compared to this baseline, emission reduction of \'adjusted LCA to determine SABA-preferred qualification\' is up to 60%, the SAF certificate can be designated as SABA-advanced\n\n• If above test is not met, the SAF certificate can be designated as SABA-eligible provided that compared to this baseline, emission reduction of \'adjusted LCA to determine SABA-eligible qualification\' is up to 60%\n\n• If above test is not met, the SAF certificate will be designated as SCS-eligible',
    required: true,
  },
  'greenhouseGas': {
    title: 'Greenhouse Gas Tab',
  },
  'greenhouseGas.totalLCAValueCorsiaDefault': {
    title: 'a. Total SAF LCA value',
    placeholder: 'Total LCA value',
    tootlipText: 'Enter the sum of all the applicable emissions and removals',
    required: true,
  },
  'greenhouseGas.totalLCAValueCorsiaActual': {
    title: 'a. Total SAF LCA value',
    placeholder: 'Total LCA value',
    tootlipText: 'Enter the sum of all the applicable emissions and removals',
    required: true,
  },
  'greenhouseGas.totalLCAValueEuDefault': {
    title: 'a. Total SAF LCA value',
    placeholder: 'Total LCA value',
    tootlipText: 'Enter the sum of all the applicable emissions and removals',
    required: true,
  },
  'greenhouseGas.totalLCAValueEuActual': {
    title: 'a. Total SAF LCA value',
    placeholder: 'Total LCA value',
    tootlipText: 'Enter the sum of all the applicable emissions and removals',
    required: true,
  },
  'greenhouseGas.coreLCAValue': {
    title: 'b. Core LCA value',
    placeholder: 'Core LCA value',
    tootlipText: 'Enter value of emissions that are not due to ILUC or displacement effects from producing the SAF batch',
    required: true,
  },
  'greenhouseGas.inducedLandUseChange': {
    title: 'c. Induced Land Use Change (ILUC) value',
    placeholder: 'Induced Land Use Change value',
    required: true,
  },
  'greenhouseGas.recyclingEmissionsCredits': {
    title: 'n. Value of Recycling Emissions Credits (RECs) \n in the total SAF LCA value',
    placeholder: 'RECs value',
    required: true,
  },
  'greenhouseGas.landfillEmissionsCredits': {
    title: 'o. Value of Landfill Emissions Credits (LECs) \n in the total SAF LCA value',
    placeholder: 'LECs value',
    required: true,
  },
  'greenhouseGas.ghgDisplacementEmissions': {
    title: 'p. GHG Emissions from displacement',
    placeholder: 'GHG value',
    required: true,
  },
  'greenhouseGas.displacementEmissions': {
    title: 'p. edi = GHG Emissions from displacement',
    placeholder: 'Edi value',
    required: true,
  },
  'greenhouseGas.production': {
    title: 'd. Production at source (e.g. feedstock cultivation)',
    placeholder: 'Production at source value',
    required: true,
  },
  'greenhouseGas.conditioning': {
    title: 'e. Conditioning at source (e.g. feedstock harvesting, collection and recovery)',
    placeholder: 'Conditioning at source value',
    required: true,
  },
  'greenhouseGas.processing': {
    title: 'f. Feedstock processing and extraction',
    placeholder: 'Feedstock processing and extraction value',
    required: true,
  },
  'greenhouseGas.transportation': {
    title: 'g. Feedstock transportation to processing and fuel production facilities',
    placeholder: 'Transaportation value',
    required: true,
  },
  'greenhouseGas.fuelTransportation': {
    title: 'i. Fuel transportation and distribution to the blend point',
    placeholder: 'Fuel transportation and distribution value',
    required: true,
  },
  'greenhouseGas.conversionProcesses': {
    title: 'h. Feedstock-to-fuel conversion processes',
    placeholder: 'Feedstock-to-fuel conversion processes value',
    required: true,
  },
  'greenhouseGas.combustion': {
    title: 'j. Fuel combustion in an aircraft engine',
    placeholder: 'Fuel combustion value',
    required: true,
  },
  'greenhouseGas.emissionsSavingsDueToSoilCarbonSequestration': {
    title: 'q. Emissions savings due to soil carbon sequestration',
    placeholder: 'Emission value',
    required: true,
  },
  'greenhouseGas.emissionsSavingsFromCSS': {
    title: 'r.  Emissions savings from carbon \n capture and storage, CCS (if applicable)',
    placeholder: 'Emissions savings from CCS',
    required: true,
  },
  'greenhouseGas.savingsFromSoil': {
    title: 'q. esca = GHG emissions savings from soil carbon accumulation via improved agricultural mgmt',
    placeholder: 'Esca value',
    required: true,
  },
  'greenhouseGas.extractionEmissions': {
    title: 'd. eec = emissions from extraction or cultivation \n of raw materials',
    placeholder: 'Eec value',
    required: true,
  },
  'greenhouseGas.annualisedEmissions': {
    title: 't. el = annualised (over 20 yrs) GHG emissions \n from carbon stock change due to land use change',
    placeholder: 'El value',
    required: true,
  },
  'greenhouseGas.processingEmissions': {
    title: 'f. ep = emissions from processing',
    placeholder: 'Ep value',
    required: true,
  },
  'greenhouseGas.transportEmissions': {
    title: 'g. etd = emissions from transport and distribution',
    placeholder: 'Etd value',
    required: true,
  },
  'greenhouseGas.inUseFuelEmissions': {
    title: 'j. eu = emissions from the fuel in use',
    placeholder: 'Eu value',
    required: true,
  },
  'greenhouseGas.savingsFromCarbonAndStorage': {
    title: 'r. eccs = Emissions savings from carbon \n capture and storage, CCS (if applicable)',
    placeholder: 'Eccs value',
    required: true,
  },
  'greenhouseGas.savingsFromCarbonAndReplacement': {
    title: 's. eccr = GHG Emissions savings from carbon \n capture and replacement',
    placeholder: 'Eccr value',
    required: true,
  },
  'greenhouseGas.savingsFromExcess': {
    title: 'u. eee = GHG emissions savings from excess \n electricity from cogeneration',
    placeholder: 'Eee value',
    required: true,
  },
  'greenhouseGas.totalLCAValueincludeEmissionsFromDeliveryToAirport': {
    title: 'k. Does the total LCA value include emissions \n from delivery to airport?',
    required: true,
  },
  'greenhouseGas.useDefaultValueForDeliveryToAirport': {
    title: 'v. Do you want to use the default 0.9 gCO2e/MJ \n value for delivery to airport?',
    required: true,
  },
  'greenhouseGas.actualGHGIntensityAirportDelivery': {
    title: 'm. The actual GHG intensity value of airport delivery \n (if not included in total LCA)',
    placeholder: 'Airport Delivery value',
    required: true,
  },
};
